import { Either, Maybe } from 'monet';
import LeftResponse from '@/storage/LeftResponse';
import { getISODateStringByDate } from '@/mixins/dateMixin';

/**
 * @description Сотрудник.
 * @param {number} id
 * @param {string} name
 * @param {?number} externalId
 * @constructor
 */
function Employee(id, name, externalId) {
  this.getId = () => id;
  this.getName = () => name;
  this.getExternalId = () => externalId;

  this.setExternalId = (value) => new this.constructor(id, name, value);
}

/**
 * @description Сотрудник внешней системы.
 * @param {number} id
 * @param {string} name
 * @constructor
 */
function ExternalEmployee(id, name) {
  this.id = id;
  this.name = name;
}

/**
 * @description Запрос на просмотр истории звонков.
 * @param {Date[]}  period
 * @param {?number} employeeId
 * @param {?number} patientId
 */
export function ViewCallHistoryRequest(group, period, employeeId, patientId) {
  this.toQuery = () => ({
    group,
    from: getISODateStringByDate(period[0]),
    to: getISODateStringByDate(period[1]),
    employee_id: employeeId,
    patient_id: patientId,
  });
}

/**
 * @description Шлюз интеграции с телефонией.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function TelephonyIntegrationGateway(axios) {
  /**
   * @description Уточнить пациента совершившего звонок.
   * @param {number} callId
   * @param {number} patientId
   * @returns {Promise<Either>}
   */
  this.clarifyIncomingCallPatient = (callId, patientId) => axios
    .put(`/api/telephony/calls/${callId}/patients/${patientId}`)
    .then(
      async (response) => Either.right(response.data.title),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Просмотр количства активных звонков от пациентов.
   * @returns {Promise<Either>}
   */
  this.viewActivePatientCallCount = () => axios.get('/api/calls/count').then(
    async (response) => Either.right(response.data),
    async (error) => {
      if (error.isAxiosError && error.response.status === 403) {
        const { causes, title } = error.response.data;
        const leftResponse = new LeftResponse(causes, title);
        return Either.left(leftResponse);
      }
      throw error;
    },
  );

  /**
   * @description Просмотр активных звонков от пациентов.
   * @returns {Promise<Either>}
   */
  this.viewActivePatientCallList = () => axios.get('/api/calls/active').then(
    async (response) => Either.right(response.data.data),
    async (error) => {
      if (error.isAxiosError && [403].includes(error.response.status)) {
        const { causes, title } = error.response.data;
        const leftResponse = new LeftResponse(causes, title);
        return Either.left(leftResponse);
      }
      throw error;
    },
  );

  /**
   * @description Просмотр интеграции c UIS.
   * @returns {Promise<Either>}
   */
  this.viewUis = () => {
    const makeEmployee = (employee) => new Employee(
      employee.id,
      employee.name,
      employee.external_id,
    );
    const makeExternalEmployee = (employee) => new ExternalEmployee(
      employee.id,
      employee.name,
    );
    return axios
      .get('/api/telephony-integrations/uis')
      .then(
        async (response) => {
          const { data } = response.data;
          const integration = {
            actions: data.actions,
            employees: (data.employees || []).map(makeEmployee),
            externalEmployees: (data.uis_employees || []).map(makeExternalEmployee),
            phone: data.phone,
            phoneList: data.phone_list,
          };
          return Either.right(integration);
        },
        async (error) => {
          if (error.isAxiosError && [400, 403].includes(error.response.status)) {
            const { causes, title } = error.response.data;
            const leftResponse = new LeftResponse(causes, title);
            return Either.left(leftResponse);
          }
          throw error;
        },
      );
  };

  /**
   * @description Просмотр интеграции c MANGO OFFICE.
   * @returns {Promise<Either>}
   */
  this.viewMangoOffice = () => {
    const makeEmployee = (employee) => new Employee(
      employee.id,
      employee.name,
      employee.external_id,
    );
    const makeExternalEmployee = (employee) => new ExternalEmployee(
      employee.id,
      employee.name,
    );
    return axios
      .get('/api/telephony-integrations/mango-office')
      .then(
        (response) => {
          const { data } = response.data;
          const integration = {
            actions: data.actions,
            employees: (data.employees || []).map(makeEmployee),
            externalEmployees: (data.external_employees || []).map(makeExternalEmployee),
          };
          return Either.right(integration);
        },
        async (error) => {
          if (error.isAxiosError && [400, 403].includes(error.response.status)) {
            const { actions, causes, title } = error.response.data;
            const leftResponse = new LeftResponse(causes, title);
            return Either.left({ actions: actions || [], error: leftResponse });
          }
          throw error;
        },
      );
  };

  /**
   * @description Создать интеграцию с MANGO OFFICE.
   * @param {any} params
   * @returns {Promise<Either>}
   */
  this.createMangoOffice = (params) => axios
    .post('/api/telephony-integrations/mango-office', params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Создать интеграцию с UIS.
   * @param {any} params
   * @returns {Promise<Either>}
   */
  this.createUis = (params) => axios
    .post('/api/telephony-integrations/uis', params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Изменить интеграцию с UIS.
   * @param {any} params
   * @returns {Promise<Either>}
   */
  this.editUis = (params) => axios
    .put('/api/telephony-integrations/uis', params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Изменить интеграцию с MANGO OFFICE.
   * @param {any} params
   * @returns {Promise<Either>}
   */
  this.editMangoOfficeIntegration = (params) => axios
    .put('/api/telephony-integrations/mango-office', params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Связать сотрудника МИС с сотрудником UIS.
   * @param {number} employeeId
   * @param {?number} externalEmployeeId
   * @returns {Promise<Either>}
   */
  this.linkEmployeeUis = (employeeId, externalEmployeeId) => axios
    .put(
      `/api/telephony-integrations/uis/employees/${employeeId}`,
      { external_employee_id: externalEmployeeId },
    )
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Связать сотрудника МИС с сотрудником UIS.
   * @param {number} employeeId
   * @param {?number} externalEmployeeId
   * @returns {Promise<Either>}
   */
  this.linkEmployeeMangoOffice = (employeeId, externalEmployeeId) => axios
    .put(
      `/api/telephony-integrations/mango-office/employees/${employeeId}`,
      { external_employee_id: externalEmployeeId },
    )
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Установить номер для исходящих звонков UIS.
   * @param {number} employeeId
   * @param {?number} externalEmployeeId
   * @returns {Promise<Either}
   */
  this.setUisOutgoingPhoneNumber = (phone) => axios
    .put('/api/telephony-integrations/uis/phone', { phone })
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Просмотр истории звонков.
   * @param {ViewCallHistoryRequest} request
   * @returns {Promise<Either}
   */
  this.viewCallHistory = (request) => {
    const makeCall = (raw) => ({
      appointment: ({
        employeeId: raw.appointment.employee_id,
        id: raw.appointment.id,
        dateIso: raw.appointment.date_iso,
        title: raw.appointment.title,
      }),
      date: raw.date,
      duration: raw.duration,
      employeeId: raw.employee_id,
      employeeName: raw.employee_name,
      id: raw.id,
      patientId: raw.patient_id,
      patientName: raw.patient_name,
      phone: raw.phone,
      recordFileLink: raw.record_file_link,
      time: raw.time,
      type: raw.type,
      typeName: raw.type_name,
    });
    return axios
      .get('/api/telephony/call-history', { params: request.toQuery() })
      .then(
        async (response) => {
          const { data } = response;
          const callGroups = (data.groups || []);
          const callHistory = (data.items || []).map(makeCall);
          return Either.right({ callGroups, callHistory });
        },
        async (error) => {
          if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
            const { causes, title } = error.response.data;
            const leftResponse = new LeftResponse(causes, title);
            return Either.left(leftResponse);
          }
          throw error;
        },
      );
  };

  /**
   * @description
   * @returns {Promise<Maybe>}
   */
  this.viewFilterForCallHistory = () => axios
    .get('/api/telephony/call-history-filters')
    .then(async (response) => Maybe.some(response.data), async () => Maybe.none());

  /**
   * @description Инициировать исходящий звонок с пациентом.
   * @param {number}  patientId
   * @param {?string} phone
   * @returns {Promise<Either>}
   */
  this.initiateCallByPatient = (patientId, phone) => axios
    .post(`/api/patients/${patientId}/calls`, { phone })
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Инициировать исходящий звонок с пациентом.
   * @param {number}  appointmentId
   * @returns {Promise<Either>}
   */
  this.initiateCallByAppointment = (appointmentId) => axios
    .post(`/api/appointments/${appointmentId}/calls`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
}
